import { Hueco } from '../../componentes/EstadoPagina';
import type { NombreIcono } from '../../componentes/iconos';
import { Segmentos } from '../../componentes/Segmentos';
import { dibujoDe } from '../../marco/iconosMenu';
import { useAnchoVentana } from '../../marco/tamano';
import type { PropsPantalla } from '../tipos';
import { APARTADOS_CLIENTES } from './apartados';
import { Lista } from './lista/Lista';
import './lista/clientes.css';

/**
 * Pantalla «Clientes» con su barra de apartados (RV Segmentos): Clientes · Duplicados · Importar · Protección de datos ·
 * Etiquetas y campos. Los apartados los declara el servidor (G141): las entradas con `parent_key: clientes` y
 * `nested_as: section`, en su orden y solo si la persona tiene su permiso; cada uno va en `?apartado=` (la ruta de su
 * entrada). Las únicas cifras son las `badge` que manda el servidor (pendientes de «Duplicados» y «Protección de
 * datos», G140), también en móvil. En móvil y en tablet, los nombres cortos «Privacidad» y «Etiquetas». El apartado
 * «Clientes» es la lista (2.1, con el alta y la ficha); el resto, la pantalla de `APARTADOS_CLIENTES` o su hueco.
 */
const CORTAS: Readonly<Record<string, string>> = {
    clientes_privacidad: 'Privacidad',
    clientes_catalogo: 'Etiquetas',
};

/** Por debajo de este ancho (tablet), la barra usa los nombres cortos. */
const ANCHO_CORTO = 1280;

interface Apartado {
    clave: string;
    etiqueta: string;
    corta?: string;
    icono: NombreIcono;
    /** Valor de `?apartado=` (null en la lista). */
    valor: string | null;
    ruta: string;
    cifra: number | null;
}

/** El `?apartado=` de la ruta de una entrada anidada (`/clientes?apartado=duplicados`); sin él, su clave sin `clientes_`. */
function valorDe(clave: string, ruta: string): string {
    const consulta = ruta.split('?')[1] ?? '';
    return new URLSearchParams(consulta).get('apartado') ?? clave.replace(/^clientes_/, '');
}

export function Clientes(props: PropsPantalla) {
    const { busqueda, ruta, navegar, movil } = props;
    const ancho = useAnchoVentana();
    const apartados: Apartado[] = [
        { clave: 'clientes', etiqueta: 'Clientes', icono: 'users', valor: null, ruta, cifra: null },
        ...(props.anidadas ?? [])
            .filter((e) => e.anidadaComo === 'section' && !e.noContratada)
            .map((e) => ({ clave: e.clave, etiqueta: e.etiqueta, corta: CORTAS[e.clave], icono: dibujoDe(e.icono), valor: valorDe(e.clave, e.ruta), ruta: e.ruta, cifra: e.contador })),
    ];
    const pedido = busqueda.get('cliente') === null ? busqueda.get('apartado') : null;
    const activo = apartados.find((a) => a.valor === pedido) ?? apartados[0];
    const indice = activo === undefined ? 0 : apartados.indexOf(activo);
    const corto = movil || ancho < ANCHO_CORTO;
    const Apartado = activo === undefined || activo.valor === null ? undefined : APARTADOS_CLIENTES[activo.clave];

    return (
        <div className="rv-clientes-pantalla">
            {apartados.length > 1 && (
                <div className="rv-cl-barra">
                    <Segmentos
                        etiqueta="Apartados de clientes"
                        movil={movil}
                        items={apartados.map((a) => ({ etiqueta: corto && !movil ? (a.corta ?? a.etiqueta) : a.etiqueta, corta: a.corta, icono: a.icono, cifra: a.cifra }))}
                        valor={indice}
                        onCambio={(i) => {
                            const a = apartados[i];
                            if (a !== undefined) {
                                navegar(a.valor === null ? ruta : a.ruta.includes('?') ? a.ruta : `${ruta}?apartado=${encodeURIComponent(a.valor)}`);
                            }
                        }}
                    />
                </div>
            )}
            {activo === undefined || activo.valor === null ? <Lista {...props} /> : Apartado !== undefined ? <Apartado {...props} /> : <Hueco seccion={activo.etiqueta} icono={activo.icono} />}
        </div>
    );
}
